import {
  commentsModeValues,
  eventSharingTypes,
  eventTagNavigationTypes,
  highlightSummaryTypes,
  layoutAuthorPositionValues,
  layoutEventTagPositionValues,
  layoutValues,
  providerConstraintValues
} from "@webng/liveblog";
import {IGameShowResponse} from "@webng-types/write-model";
import {colorRegex} from "./shared";
import z from "zod";
import z4, {ParsePayload} from 'zod/v4/core'
import {createPartialParser, removeNullValues, zBooleanWrapper} from "./utils";

export const refreshValues = ["auto", "notify", "off"] as const
export type RefreshType = typeof refreshValues[number]

export const sortValues = ["asc", "desc"] as const
export type SortOrder = typeof sortValues[number]

export const sortControlsValues = ["off", "show"] as const
export type SortControlsType = typeof sortControlsValues[number]


// const deprecatedAutomaticSummaryTypes = ["chapters", "highlights", "highlights-slider", "game-summary", "none"] as const
// type DeprecatedAutomaticSummaryType = typeof deprecatedAutomaticSummaryTypes[number]

export const liveblogWidgetConfigurationLimitMin = 1;
export const liveblogWidgetConfigurationLimitMax = 1000;


const webEmbedConstraintPreprocessor = (val: unknown, ctx: z4.$RefinementCtx<unknown>) => {
  try {
    if(val === undefined || val === null || val === "") {
      return {}
    } else if(typeof val == "string") {
      return JSON.parse(val)
    } else if(typeof val == "object") {
      return val
    } else {
      ctx.addIssue({
        code: "custom",
        message: "Invalid JSON or shape",
      });
      return z.NEVER;
    }
  } catch(e) {
    ctx.addIssue({
      code: "custom",
      message: "Invalid JSON or shape",
    });
    return z.NEVER;
  }
}


function upgradeAutomaticSummary(arg: unknown, ctx: ParsePayload): unknown {
  if(typeof arg === 'object' && arg && 'automaticSummary' in arg) {
        const {automaticSummary, ...rest} = arg

    switch(automaticSummary) {
      case "game-summary":
        return {
          ...rest,
          highlightSummaryType: "none",
          sportsShowScorerList: true,
          sportsShowScoreboard: true
        }
      case "none":
        return {
          ...rest,
          highlightSummaryType: "none",
          sportsShowScorerList: false,
          sportsShowScoreboard: true
        }
      case "highlights":
      case "highlights-slider":
      case "chapters":
        return {
          ...rest,
          highlightSummaryType: automaticSummary,
          sportsShowScorerList: false,
          sportsShowScoreboard: true
        }
      default:
        ctx.issues.push({
          code: "custom",
          message: "Invalid automaticSummary value",
          input: automaticSummary,
          path: ["automaticSummary"]
        })
        return z.NEVER;
    }
  } else {
    return arg
  }
}

export function preprocessLiveblogThemeConfiguration(arg: unknown, ctx: ParsePayload): unknown {
  const step1 = removeNullValues(arg, ctx)
  const step2 = upgradeAutomaticSummary(step1, ctx)
  return step2
}

export const rawLiveblogThemeConfigurationSchema = z.object({
  refresh: z.enum(refreshValues).default("notify"),
  sort: z.enum(sortValues).default("desc"),
  sortControls: z.enum(sortControlsValues).default("off"),
  limit: z.coerce.number().int().min(1).max(1000).default(15),
  deepLinkLimit: z.coerce.number().int().min(0).max(1000).default(50),
  deepLinkDepth: z.coerce.number().int().min(0).max(1000).default(0),
  webEmbedDefaultConstraint: z.enum(providerConstraintValues).default("consent"),
  webEmbedConstraints: z.preprocess(webEmbedConstraintPreprocessor, z.record(z.string(), z.union([z.enum(providerConstraintValues), z.undefined()]))),
  useSlideshow: zBooleanWrapper(true),
  automaticSummaryHighlightsLimit: z.coerce.number().int().min(1).max(25).default(8),
  eventTagNavigation: z.enum(eventTagNavigationTypes).default("filter-list"),
  sharing: z.enum(eventSharingTypes).default("off"),
  layout: z.enum(layoutValues).default("default"),
  layoutAuthorPosition: z.enum(layoutAuthorPositionValues).default("top"),
  layoutEventTagPosition: z.enum(layoutEventTagPositionValues).default("top"),
  styleUseCards: zBooleanWrapper(),
  styleInvertTime: zBooleanWrapper(),
  styleInvertHighlightEvents: zBooleanWrapper(),
  styleInvertStickyEvents: zBooleanWrapper(),
  styleInvertSummaryEvents: zBooleanWrapper(),
  stylePrimaryColor: z.string().regex(colorRegex).default(""),
  stylePrimaryColorDark: z.string().regex(colorRegex).default(""),
  styleSecondaryColor: z.string().regex(colorRegex).default(""),
  styleSecondaryColorDark: z.string().regex(colorRegex).default(""),
  styleTextColor: z.string().regex(colorRegex).default(""),
  styleTextColorDark: z.string().regex(colorRegex).default(""),
  styleTextMutedColor: z.string().regex(colorRegex).default(""),
  styleTextMutedColorDark: z.string().regex(colorRegex).default(""),
  styleBgColor: z.string().regex(colorRegex).default(""),
  styleBgColorDark: z.string().regex(colorRegex).default(""),
  styleEventHighlightBgColor: z.string().regex(colorRegex).default(""),
  styleEventHighlightBgColorDark: z.string().regex(colorRegex).default(""),
  styleEventStickyBgColor: z.string().regex(colorRegex).default(""),
  styleEventStickyBgColorDark: z.string().regex(colorRegex).default(""),
  styleEventSummaryBgColor: z.string().regex(colorRegex).default(""),
  styleEventSummaryBgColorDark: z.string().regex(colorRegex).default(""),
  commentsMode: z.enum(commentsModeValues).default("default"),

  highlightSummaryType: z.enum(highlightSummaryTypes).default("chapters"),
  sportsShowScoreboard: zBooleanWrapper(true),
  sportsShowScorerList: zBooleanWrapper(),

  injectSchemaOrg: z.enum(["off", "auto", "always"]).default("auto")
})

export type LiveblogThemeConfiguration = z.infer<typeof rawLiveblogThemeConfigurationSchema>

export const liveblogThemeConfigurationSchema = z.preprocess(preprocessLiveblogThemeConfiguration, rawLiveblogThemeConfigurationSchema)

export const parsePartialLiveblogThemeConfiguration = createPartialParser<LiveblogThemeConfiguration>(rawLiveblogThemeConfigurationSchema, preprocessLiveblogThemeConfiguration, {
  automaticSummary: z.string()
})

// Liveblog Core Widget

export interface LiveblogCoreWidgetInitialData {
  gameShowResponse?: IGameShowResponse
  uniqueId?: string
  isSSR?: boolean
}

// Liveblog Widget

export interface LiveblogWidgetInitialData extends LiveblogCoreWidgetInitialData {
  configuration?: LiveblogThemeConfiguration
  css?: string
  liveblogUrl?: string
}
